import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Swords, Bot, Trophy, Zap, Flame, Crown, ChevronRight, Sparkles, Target, Users, Star, Shield,
  Mail, Phone, Instagram, MapPin, BookOpen, Rocket, CalendarCheck, Music2,
} from "lucide-react";
import Btn from "../components/Btn";
import LandingNav from "../components/LandingNav";
import api, { API } from "../lib/api";

const DEFAULT_EXAM_DATE = "2026-10-01T08:00:00+07:00";

function useCountdown(target) {
  const [t, setT] = useState(calc(target));
  useEffect(() => {
    const id = setInterval(() => setT(calc(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}
function calc(target) {
  const diff = Math.max(0, target - new Date());
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
}

const FEATURES = [
  { icon: Swords, color: "var(--su-primary)", title: "1v1 Quiz Arena", desc: "Adu cepat jawab soal lawan pengguna lain secara real-time. Menang, naik ELO!", tag: "Multiplayer" },
  { icon: Bot, color: "var(--su-success)", title: "AI Personal Mentor", desc: "Salah jawab? Tekan 'Jelaskan' dan AI membedah langkah demi langkah.", tag: "AI" },
  { icon: Trophy, color: "var(--su-secondary)", title: "RPG Progression", desc: "Kumpulkan EXP, naik level, buka skill tree, dan koleksi badge langka.", tag: "Gamified" },
  { icon: Target, color: "var(--su-energy)", title: "CBT Simulator", desc: "Simulasi mirip aslinya: timer per sesi, IRT SNBT & passing grade CPNS.", tag: "Realistis" },
  { icon: BookOpen, color: "var(--su-primary)", title: "Modul Materi", desc: "Rangkuman lengkap TWK, TIU, TKP, PU, PPU, PBM & PK siap dibaca kapan saja.", tag: "Belajar" },
  { icon: Users, color: "var(--su-success)", title: "Study Guild", desc: "Gabung guild, chat real-time, kerja bareng, dan menangkan Guild War bersama tim.", tag: "Komunitas" },
  { icon: Rocket, color: "var(--su-danger)", title: "Tryout Akbar", desc: "Simulasi serentak se-Indonesia dengan poster event & peringkat nasional. Bagikan untuk tiket gratis!", tag: "Event" },
  { icon: CalendarCheck, color: "var(--su-secondary)", title: "Misi Harian", desc: "Selesaikan misi tiap hari untuk klaim token, energi & EXP. Jaga streak-mu tetap menyala!", tag: "Reward" },
];;

export default function Landing() {
  const [board, setBoard] = useState([]);
  const [testis, setTestis] = useState([]);
  const [guilds, setGuilds] = useState([]);
  const [contact, setContact] = useState(null);
  const [products, setProducts] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setBoard(data.leaderboard.slice(0, 5))).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestis(data.testimonials)).catch(() => {});
    api.get("/public/guilds").then(({ data }) => setGuilds(data.guilds)).catch(() => {});
    api.get("/site-settings").then(({ data }) => setContact(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data.products)).catch(() => {});
    api.get("/public/success-stories").then(({ data }) => setStories(data.stories)).catch(() => {});
  }, []);

  const targetDate = new Date(contact?.countdown_target || DEFAULT_EXAM_DATE);
  const c = useCountdown(targetDate.getTime());
  const showCountdown = contact ? contact.countdown_enabled !== false : true;
  const countdownLabel = contact?.countdown_label || "Menuju SKD CPNS 2026";

  const bannerTarget = new Date(contact?.banner_target || DEFAULT_EXAM_DATE);
  const bc = useCountdown(bannerTarget.getTime());
  const showBanner = contact ? contact.banner_enabled !== false : false;

  return (
    <div className="min-h-screen bg-su-bg text-su-text overflow-x-hidden">
      <LandingNav />

      {/* akbar promo banner */}
      {showBanner && (
        <Link to="/auth" data-testid="akbar-banner" className="block group">
          <div className="relative overflow-hidden bg-gradient-to-r from-su-primary via-su-secondary to-su-danger">
            <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-center sm:text-left">
              <div className="flex items-center gap-3 min-w-0">
                <Rocket className="w-6 h-6 text-white shrink-0 hidden sm:block" />
                <div className="min-w-0">
                  <p className="font-heading font-black text-white text-base sm:text-lg leading-tight truncate">{contact.banner_title}</p>
                  <p className="font-medium text-white/85 text-xs sm:text-sm truncate">{contact.banner_subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex gap-1.5">
                  {[["H", bc.d], ["J", bc.h], ["M", bc.m], ["D", bc.s]].map(([l, v]) => (
                    <div key={l} className="bg-black/25 backdrop-blur rounded-lg px-2 py-1 text-center min-w-[38px]" data-testid={`banner-cd-${l}`}>
                      <div className="font-mono font-black text-white text-sm leading-none">{String(v).padStart(2, "0")}</div>
                      <div className="text-[9px] font-bold text-white/70 uppercase">{l}</div>
                    </div>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1 bg-white text-su-primary font-heading font-black text-sm px-4 py-2 rounded-xl shadow-md group-hover:scale-105 transition-transform whitespace-nowrap">
                  {contact.banner_cta || "Ikut Sekarang"} <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* hero */}
      <section className="max-w-7xl mx-auto px-4 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 bg-su-secondary/20 text-su-secondaryShadow px-3 py-1.5 rounded-full font-heading font-extrabold text-xs mb-5">
            <Sparkles className="w-4 h-4" /> Belajar rasa main game
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Naikkan Skor <span className="text-su-primary">SNBT</span> & <span className="text-su-success">CPNS</span>-mu bareng SkorUp.io
          </h1>
          <p className="text-su-muted font-medium text-base lg:text-lg mt-5 max-w-xl">
            Platform tryout gamified untuk SNBT, CPNS, dan Sekolah Kedinasan. Tanpa tutor, cukup AI, tryout adaptif, dan arena kompetitif.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/auth"><Btn data-testid="hero-cta" size="lg">Mulai Gratis <ChevronRight className="w-5 h-5" /></Btn></Link>
            <a href="#fitur"><Btn variant="ghost" size="lg">Lihat Fitur</Btn></a>
          </div>
          <div className="flex items-center gap-5 mt-8 text-sm font-heading font-extrabold text-su-muted">
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-su-primary" /> 12.480+ pejuang</span>
            <span className="flex items-center gap-1.5"><Flame className="w-4 h-4 text-orange-500 fill-current" /> 3.200+ soal</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="relative">
          <div className="relative rounded-[2rem] overflow-hidden border-2 border-su-border shadow-[0_8px_0_var(--su-border)]">
            <img src="https://images.unsplash.com/photo-1637166185518-058f5896a2e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=940&h=650&fit=crop" alt="Futuristik" className="w-full h-[360px] object-cover" />
          </div>
          {/* countdown floating card */}
          {showCountdown && (
          <motion.div animate={{ y: [0, -12, 0] }} transition={{ repeat: Infinity, duration: 4 }}
            className="absolute -bottom-6 -left-4 sm:left-6 card-chunky p-4 w-[260px]" data-testid="landing-countdown">
            <p className="text-xs font-heading font-extrabold uppercase tracking-wider text-su-muted mb-2">⏳ {countdownLabel}</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Hari", c.d], ["Jam", c.h], ["Mnt", c.m], ["Dtk", c.s]].map(([l, v]) => (
                <div key={l} className="bg-su-primary/10 rounded-xl py-2">
                  <div className="font-mono font-bold text-xl text-su-primary" data-testid={`countdown-${l}`}>{String(v).padStart(2, "0")}</div>
                  <div className="text-[10px] font-bold text-su-muted uppercase">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
          )}
          <motion.div animate={{ y: [0, 12, 0] }} transition={{ repeat: Infinity, duration: 3.5 }}
            className="absolute -top-4 -right-2 sm:right-4 card-chunky px-4 py-3 flex items-center gap-2">
            <Zap className="w-5 h-5 text-su-energy fill-current" />
            <span className="font-heading font-black">+250 EXP</span>
          </motion.div>
        </motion.div>
      </section>

      {/* hero testimonial carousel */}
      {testis.length > 0 && (
        <section className="border-y border-su-border bg-su-surface/30 py-4 overflow-hidden" data-testid="hero-testimonial-carousel">
          <div className="max-w-7xl mx-auto px-4 mb-2 flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-su-muted">
            <Star className="w-3.5 h-3.5 text-su-energy fill-su-energy" /> Sorotan ulasan bintang 5
          </div>
          <div className="marquee-mask">
            <div className="marquee-track gap-4 px-4">
              {[...testis, ...testis].map((t, i) => (
                <div key={i} className="card-chunky px-5 py-3 flex items-center gap-3 w-[300px] shrink-0" data-testid={`hero-testi-${i}`}>
                  <div className="w-10 h-10 rounded-full bg-su-primary/20 grid place-items-center font-heading font-bold text-su-primary shrink-0">{t.user_name?.[0] || "U"}</div>
                  <div className="min-w-0">
                    <div className="flex gap-0.5 mb-0.5">{[...Array(5)].map((_, s) => <Star key={s} className="w-3 h-3 text-su-energy fill-su-energy" />)}</div>
                    <p className="text-sm font-medium truncate">"{t.comment}"</p>
                    <span className="text-xs text-su-muted font-bold">{t.user_name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* features bento */}
      <section id="fitur" className="max-w-7xl mx-auto px-4 py-14">
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-center mb-3">Killer Features 🔥</h2>
        <p className="text-su-muted font-medium text-center mb-10 max-w-2xl mx-auto">Semua yang bikin belajar nagih dan skormu meroket.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="card-chunky p-6 hover:-translate-y-1 transition-transform">
              <div className="w-14 h-14 rounded-2xl grid place-items-center mb-4" style={{ background: `${f.color}22` }}>
                <f.icon className="w-7 h-7" style={{ color: f.color }} />
              </div>
              <span className="text-[10px] font-heading font-black uppercase tracking-wider" style={{ color: f.color }}>{f.tag}</span>
              <h3 className="font-heading font-black text-xl mt-1 mb-2">{f.title}</h3>
              <p className="text-su-muted font-medium text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* leaderboard peek */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="card-chunky overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-10">
              <div className="inline-flex items-center gap-2 bg-su-secondary/20 text-su-secondaryShadow px-3 py-1.5 rounded-full font-heading font-extrabold text-xs mb-4">
                <Trophy className="w-4 h-4" /> Papan Peringkat Nasional
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl mb-3">Kamu bisa di puncak?</h2>
              <p className="text-su-muted font-medium mb-6">Ikuti Mega Tryout (Tryout Akbar) dan buktikan kamu yang terbaik se-Indonesia. Peringkat live, hadiah nyata.</p>
              <Link to="/auth"><Btn data-testid="leaderboard-cta">Gabung & Panjat Ranking</Btn></Link>
            </div>
            <div className="bg-su-bg p-6 sm:p-8 border-t-2 lg:border-t-0 lg:border-l-2 border-su-border">
              <div className="space-y-2">
                {(board.length ? board : DUMMY).map((r, i) => (
                  <div key={i} className="flex items-center gap-3 bg-su-surface rounded-2xl p-3 border-2 border-su-border">
                    <span className={`w-8 h-8 grid place-items-center rounded-xl font-heading font-black text-sm ${
                      i === 0 ? "bg-su-secondary text-black" : i === 1 ? "bg-slate-300 text-black" : i === 2 ? "bg-orange-400 text-white" : "bg-su-track text-su-muted"
                    }`}>{r.rank || i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-extrabold truncate flex items-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}</div>
                      <div className="text-xs text-su-muted font-bold">Level {r.level} · {r.exp?.toLocaleString?.() || r.exp} EXP</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* guild gallery */}
      {guilds.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-14" data-testid="guild-gallery">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-center mb-3">Guild Unggulan 🛡️</h2>
          <p className="text-su-muted font-medium text-center mb-10">Komunitas belajar paling aktif minggu ini beserta koleksi lencananya.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {guilds.map((g) => (
              <div key={g.guild_id} className="card-chunky p-6" data-testid={`gallery-guild-${g.guild_id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {g.has_photo ? (
                    <img src={`${API}/guilds/${g.guild_id}/photo`} alt="" className="w-14 h-14 rounded-2xl object-cover border border-su-border" />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl grid place-items-center text-black font-heading font-bold text-xl" style={{ background: g.emblem_color }}>{g.name[0]}</div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-heading font-bold text-lg truncate flex items-center gap-1"><Shield className="w-4 h-4 text-su-primary" />{g.name}</h3>
                    <p className="text-xs text-su-muted font-bold flex items-center gap-2">
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{g.members}</span>
                      <span className="flex items-center gap-1 text-su-energy"><Flame className="w-3.5 h-3.5 fill-current" />{g.weekly_exp.toLocaleString()}/mgg</span>
                    </p>
                  </div>
                </div>
                {g.bio && <p className="text-sm text-su-muted mb-3 line-clamp-2">{g.bio}</p>}
                {g.badges.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {g.badges.map((b) => (
                      <span key={b.id} title={b.name} className="inline-flex items-center gap-1 text-[10px] font-heading font-bold px-2 py-1 rounded-full border border-su-border text-su-muted">
                        <Star className="w-3 h-3 text-su-energy" />{b.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* success stories (Testimoni Keberhasilan) */}
      {stories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-14" data-testid="success-stories">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-center mb-3">Mereka Berhasil Lolos 🎉</h2>
          <p className="text-su-muted font-medium text-center mb-10">Kisah nyata pejuang yang lolos bersama SkorUp.io.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {stories.slice(0, 6).map((s) => (
              <div key={s.id} className="card-chunky p-6" data-testid={`home-story-${s.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <img src={`${API}/success-stories/${s.id}/photo`} alt={s.name}
                    className="w-14 h-14 rounded-2xl object-cover border-2 border-su-border"
                    onError={(e) => { e.currentTarget.src = "/brand/skorup_icon.png"; }} />
                  <div className="min-w-0">
                    <p className="font-heading font-black truncate">{s.name}</p>
                    {s.position && <p className="text-xs font-heading font-bold text-su-success truncate">{s.position}</p>}
                  </div>
                </div>
                <p className="text-su-text font-medium text-sm leading-relaxed line-clamp-5">"{s.comment}"</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8"><Link to="/testimoni"><Btn variant="ghost" data-testid="home-story-more">Lihat Semua Testimoni <ChevronRight className="w-4 h-4" /></Btn></Link></div>
        </section>
      )}

      {/* testimonials */}
      {testis.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-14" data-testid="testimonials">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-center mb-3">Kata Mereka ⭐</h2>
          <p className="text-su-muted font-medium text-center mb-10">Ulasan bintang 5 dari para pejuang SkorUp.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testis.map((t, i) => (
              <div key={i} className="card-chunky p-6" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 text-su-energy fill-su-energy" />)}
                </div>
                <p className="text-su-text font-medium mb-4">"{t.comment}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-su-primary/20 grid place-items-center font-heading font-bold text-su-primary">{t.user_name?.[0] || "U"}</div>
                  <span className="font-heading font-bold text-sm">{t.user_name}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* pricing preview */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-center mb-3">Pilih Paketmu</h2>
        <p className="text-su-muted font-medium text-center mb-10">Mulai gratis, upgrade kapan saja.</p>
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto" data-testid="landing-pricing">
          {/* freemium */}
          <div className="card-chunky p-6 flex flex-col" data-testid="plan-freemium">
            <h3 className="font-heading font-black text-xl">Freemium</h3>
            <div className="my-3"><span className="font-heading font-black text-3xl">Rp0</span><span className="text-su-muted font-bold text-sm">/selamanya</span></div>
            <ul className="space-y-2 text-sm font-medium text-su-muted flex-1 mb-5">
              {["5 token awal + energi 100", "Tryout, Drill & Arena", "Leaderboard nasional", "Modul materi — terkunci 🔒", "AI Mentor — terkunci 🔒"].map((x) => (
                <li key={x} className="flex items-start gap-2"><span className="text-su-success font-black">✓</span>{x}</li>
              ))}
            </ul>
            <Link to="/auth"><Btn variant="ghost" className="w-full">Mulai Gratis</Btn></Link>
          </div>
          {/* real products */}
          {products.map((p) => {
            const isVip = p.type === "subscription";
            return (
              <div key={p.product_id} className={`card-chunky p-6 flex flex-col ${isVip ? "ring-4 ring-su-primary/30 relative" : ""}`} data-testid={`plan-${p.product_id}`}>
                {p.discount_label ? <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-su-secondary text-black text-xs font-heading font-black px-3 py-1 rounded-full">{p.discount_label}</span>
                  : isVip && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-su-primary text-white text-xs font-heading font-black px-3 py-1 rounded-full">TERPOPULER</span>}
                <h3 className="font-heading font-black text-xl">{p.name}</h3>
                <div className="my-3"><span className="font-heading font-black text-3xl">Rp{Number(p.amount).toLocaleString("id-ID")}</span><span className="text-su-muted font-bold text-sm">{isVip ? (p.product_id.includes("yearly") ? "/tahun" : "/bulan") : ""}</span></div>
                <ul className="space-y-2 text-sm font-medium text-su-muted flex-1 mb-5">
                  {(p.features?.length ? p.features : [
                    p.grant?.tokens ? `${p.grant.tokens} token` : null,
                    p.grant?.energy ? `Energi ${p.grant.energy}` : null,
                  ].filter(Boolean)).map((x) => <li key={x} className="flex items-start gap-2"><span className="text-su-success font-black">✓</span>{x}</li>)}
                </ul>
                <Link to="/auth"><Btn variant={isVip ? "primary" : "ghost"} className="w-full">{isVip ? "Jadi VIP" : "Beli Token"}</Btn></Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* contact */}
      <section id="kontak" className="max-w-7xl mx-auto px-4 py-14" data-testid="contact-section">
        <div className="card-chunky p-8 sm:p-10 text-center">
          <div className="inline-flex items-center gap-2 bg-su-primary/15 text-su-primary px-3 py-1.5 rounded-full font-heading font-extrabold text-xs mb-4">
            <Mail className="w-4 h-4" /> Hubungi Kami
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl mb-3">Butuh bantuan? Sapa kami!</h2>
          <p className="text-su-muted font-medium mb-6 max-w-xl mx-auto">Tim SkorUp siap membantu pertanyaan seputar akun, pembayaran, dan tryout.</p>
          {(contact?.instagram || contact?.tiktok) && (
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8" data-testid="landing-socials">
              <span className="font-heading font-black text-sm text-su-muted">Follow kami:</span>
              {contact?.instagram && (
                <a href={`https://instagram.com/${contact.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" data-testid="social-instagram"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-heading font-extrabold text-sm text-white transition-transform hover:scale-105" style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }}>
                  <Instagram className="w-4 h-4" /> {contact.instagram}
                </a>
              )}
              {contact?.tiktok && (
                <a href={`https://www.tiktok.com/@${contact.tiktok.replace(/^@/, "")}`} target="_blank" rel="noreferrer" data-testid="social-tiktok"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-heading font-extrabold text-sm text-white bg-black transition-transform hover:scale-105">
                  <Music2 className="w-4 h-4" /> {contact.tiktok}
                </a>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {contact?.email && (
              <a href={`mailto:${contact.email}`} data-testid="contact-email" className="card-chunky p-5 hover:-translate-y-1 transition-transform flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-2xl bg-su-primary/15 grid place-items-center"><Mail className="w-6 h-6 text-su-primary" /></div>
                <span className="text-xs font-heading font-black uppercase text-su-muted">Email</span>
                <span className="font-heading font-bold text-sm break-all">{contact.email}</span>
              </a>
            )}
            {contact?.whatsapp && (
              <a href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="card-chunky p-5 hover:-translate-y-1 transition-transform flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-2xl bg-su-success/15 grid place-items-center"><Phone className="w-6 h-6 text-su-success" /></div>
                <span className="text-xs font-heading font-black uppercase text-su-muted">WhatsApp</span>
                <span className="font-heading font-bold text-sm">{contact.whatsapp}</span>
              </a>
            )}
            {contact?.instagram && (
              <a href={`https://instagram.com/${contact.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="card-chunky p-5 hover:-translate-y-1 transition-transform flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-2xl bg-su-secondary/15 grid place-items-center"><Instagram className="w-6 h-6 text-su-secondaryShadow" /></div>
                <span className="text-xs font-heading font-black uppercase text-su-muted">Instagram</span>
                <span className="font-heading font-bold text-sm">{contact.instagram}</span>
              </a>
            )}
            {contact?.address && (
              <div data-testid="contact-address" className="card-chunky p-5 flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-2xl bg-su-energy/15 grid place-items-center"><MapPin className="w-6 h-6 text-su-energy" /></div>
                <span className="text-xs font-heading font-black uppercase text-su-muted">Lokasi</span>
                <span className="font-heading font-bold text-sm">{contact.address}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-su-border py-8 text-center">
        <p className="font-heading font-extrabold text-su-muted">SkorUp<span className="text-su-primary">.io</span> © 2026 · Level up your score.</p>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
          <Link to="/faq" data-testid="footer-faq" className="text-sm font-heading font-bold text-su-muted hover:text-su-primary transition-colors">FAQ</Link>
          <span className="text-su-border">·</span>
          <Link to="/terms" data-testid="footer-terms" className="text-sm font-heading font-bold text-su-muted hover:text-su-primary transition-colors">Syarat & Ketentuan</Link>
          <span className="text-su-border">·</span>
          <Link to="/refund" data-testid="footer-refund" className="text-sm font-heading font-bold text-su-muted hover:text-su-primary transition-colors">Kebijakan Refund</Link>
          <span className="text-su-border">·</span>
          <Link to="/contact" data-testid="footer-contact" className="text-sm font-heading font-bold text-su-muted hover:text-su-primary transition-colors">Kontak</Link>
        </div>
        {contact?.email && <p className="text-sm text-su-muted font-medium mt-3" data-testid="footer-email">{contact.email}{contact?.whatsapp ? ` · ${contact.whatsapp}` : ""}</p>}
        <p className="text-xs text-su-muted font-medium mt-3" data-testid="footer-company">© 2026 PT. Ruang Karya Teknologi. All Rights Reserved.</p>
      </footer>
    </div>
  );
}

const DUMMY = [
  { name: "Rizky Pratama", level: 24, exp: 14200, vip: true },
  { name: "Siti Nurhaliza", level: 22, exp: 12800, vip: true },
  { name: "Andi Wijaya", level: 20, exp: 11050, vip: true },
  { name: "Dewi Lestari", level: 18, exp: 9400 },
  { name: "Bagus Santoso", level: 17, exp: 8600 },
];

const PLANS_UNUSED_REMOVED = true;
